import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useTheme } from "../../contexts/ThemeContext";
import { MessageCircle, Eye, Pin, Trash2, Plus } from "lucide-react";
import { formatTimeAgo, getCategoryColor } from "./utils";

export const DiscussionList = ({
  discussions,
  categoryName,
  onTogglePin,
  onDeleteDiscussion,
  canPin,
  onNewDiscussion,
  isCreating = false,
}) => {
  const theme = useTheme();
  const auth = useAuth();
  const navigate = useNavigate();
  const isDark = theme.mode === "dark";
  const currentUserId = auth.user?.id;

  const categoryColor = getCategoryColor(categoryName || "general");

  if (!discussions || discussions.length === 0) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "64px 24px",
          textAlign: "center",
          backgroundColor: isDark ? "rgba(17, 18, 31, 0.85)" : (theme.colors.cardBackground || "#ffffff"),
          border: `1px dashed ${isDark ? "rgba(255, 255, 255, 0.2)" : "#cbd5e1"}`,
          borderRadius: "16px",
          gap: "16px",
          marginTop: "12px",
          fontFamily: "'Montserrat', sans-serif",
        }}
      >
        <div
          style={{
            width: "68px",
            height: "68px",
            borderRadius: "50%",
            backgroundColor: "rgba(236, 178, 54, 0.12)",
            border: "1px solid rgba(236, 178, 54, 0.25)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#ECB236",
          }}
        >
          <MessageCircle size={34} />
        </div>
        <div>
          <h3
            style={{
              margin: 0,
              color: theme.colors.textColor || (isDark ? "#ffffff" : "#1e293b"),
              fontSize: "var(--font-size-lg, 20px)",
              fontWeight: 700,
            }}
          >
            Aún no se ha creado nada
          </h3>
          <p
            style={{
              margin: "8px 0 0",
              color: isDark ? "#94a3b8" : "#64748b",
              fontSize: "var(--font-size-body, 16px)",
              maxWidth: "460px",
              lineHeight: 1.5,
            }}
          >
            {isCreating
              ? "Completá el formulario superior para publicar el primer debate en esta categoría."
              : "No hay discusiones activas en esta categoría. ¡Sé el primero en iniciar una conversación!"}
          </p>
        </div>
        {!isCreating && onNewDiscussion && (
          <button
            type="button"
            onClick={onNewDiscussion}
            style={{
              marginTop: "8px",
              padding: "12px 24px",
              borderRadius: "10px",
              border: "none",
              backgroundColor: "#ECB236",
              color: "#11121f",
              fontWeight: 700,
              fontSize: "var(--font-size-body, 16px)",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              boxShadow: "0 4px 12px rgba(236, 178, 54, 0.3)",
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#f5be47";
              e.currentTarget.style.transform = "translateY(-1px)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "#ECB236";
              e.currentTarget.style.transform = "translateY(0)";
            }}
          >
            <Plus size={18} />
            Crear primera discusión
          </button>
        )}
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      <style>{`
        @keyframes slideInCard {
          from {
            opacity: 0;
            transform: translateY(-15px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
      {discussions.map((discussion, index) => {
        const isPinned = discussion.is_pinned;
        const isAuthor = Boolean(currentUserId && Number(discussion.author_id) === Number(currentUserId));
        const hasStaffRole = Boolean(
          auth.user?.roles?.includes("admin") || 
          auth.user?.roles?.includes("editingteacher") || 
          auth.user?.roles?.includes("teacher")
        );
        const canDelete = isAuthor || hasStaffRole;
        return (
          <div
            key={discussion.id}
            onClick={() => navigate(`/foro/discussion/${discussion.id}`)}
            style={{
              backgroundColor: isPinned ? `${categoryColor}15` : (isDark ? "rgba(17, 18, 31, 0.85)" : (theme.colors.cardBackground || "#ffffff")),
              border: isPinned ? `2px solid ${categoryColor}` : "1px solid rgba(255,255,255,0.2)",
              borderRadius: "12px",
              padding: "20px",
              cursor: "pointer",
              transition: "all 0.2s ease",
              boxShadow: isPinned
                ? `0 4px 12px ${categoryColor}20`
                : "0 2px 8px rgba(0,0,0,0.08)",
              position: "relative",
              overflow: "hidden",
              animation: `slideInCard 0.3s ease-out ${index * 0.05}s both`,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = isPinned
                ? `0 8px 24px ${categoryColor}40`
                : "0 4px 16px rgba(0,0,0,0.12)";
              e.currentTarget.style.borderColor = "#ECB236";
              if (isPinned) {
                e.currentTarget.style.backgroundColor = `${categoryColor}25`;
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = isPinned
                ? `0 4px 12px ${categoryColor}20`
                : "0 2px 8px rgba(0,0,0,0.08)";
              e.currentTarget.style.borderColor = isPinned ? categoryColor : "rgba(255,255,255,0.2)";
              if (isPinned) {
                e.currentTarget.style.backgroundColor = `${categoryColor}15`;
              }
            }}
          >
            {/* Main row: Avatar + Content */}
            <div style={{ display: "flex", gap: "16px" }}>
                {/* Large Avatar */}
              <img
                src={discussion.author_picture || `https://udalba.tipddy.cl/theme/image.php/boost/core/1/u/f1`}
                alt={discussion.author_name}
                style={{
                  width: "48px",
                  height: "48px",
                  borderRadius: "50%",
                  objectFit: "cover",
                  border: `2px solid ${isDark ? "#334155" : "#e2e8f0"}`,
                  flexShrink: 0,
                }}
              />

              {/* Content */}
              <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "8px" }}>
                {/* Meta row */}
                <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                  {/* Category badge */}
                  <span
                    style={{
                      backgroundColor: `${categoryColor}20`,
                      color: categoryColor,
                      padding: "6px 14px",
                      borderRadius: "12px",
                      fontSize: "var(--font-size-min, 15px)",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                    }}
                  >
                    {categoryName || "General"}
                  </span>
                  
                  <span style={{ color: isDark ? "#94a3b8" : "#64748b", fontSize: "var(--font-size-min, 15px)" }}>
                    • Por {discussion.author_name}
                  </span>
                  
                  <span style={{ color: isDark ? "#64748b" : "#94a3b8", fontSize: "var(--font-size-min, 15px)" }}>
                    • {formatTimeAgo(discussion.created_at)}
                  </span>

                  {/* Pin indicator */}
                  {isPinned && (
                    <span style={{ color: categoryColor, fontSize: "var(--font-size-min, 15px)", fontWeight: 600, marginLeft: "auto" }}>
                      <Pin size={16} style={{ display: "inline", marginRight: "4px" }} />
                      FIJADO
                    </span>
                  )}
                </div>

                {/* Title */}
                <h3
                  className="font-outfit"
                  style={{
                    color: theme.colors.textColor || theme.colors.textColorNavbar,
                    margin: 0,
                    fontSize: "var(--font-size-lg, 20px)",
                    fontWeight: 600,
                    lineHeight: 1.3,
                    letterSpacing: "-0.01em",
                  }}
                >
                  {discussion.title}
                </h3>
              </div>
            </div>

            {/* Footer: Stats */}
            <div style={{ display: "flex", alignItems: "center", gap: "20px", marginTop: "12px", paddingLeft: "64px" }}>
              <span
                style={{
                  color: isDark ? "#94a3b8" : "#64748b",
                  fontSize: "var(--font-size-min, 15px)",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <MessageCircle size={18} />
                {discussion.replies_count || 0} respuestas
              </span>
              <span
                style={{
                  color: isDark ? "#94a3b8" : "#64748b",
                  fontSize: "var(--font-size-min, 15px)",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <Eye size={18} />
                {discussion.views_count || 0} vistas
              </span>

              {/* Pin button for admin */}
              {canPin && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onTogglePin(discussion.id);
                  }}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: "4px",
                    color: isPinned ? categoryColor : (isDark ? "#64748b" : "#94a3b8"),
                    transition: "color 0.2s",
                  }}
                  title={isPinned ? "Despinear" : "Pinear"}
                >
                  <Pin size={16} />
                </button>
              )}
              
              {/* Delete button for admin or discussion author */}
              {canDelete && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteDiscussion(discussion);
                  }}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: "4px",
                    color: "#ef4444",
                    transition: "color 0.2s",
                    marginLeft: "auto",
                  }}
                  title={hasStaffRole ? "Eliminar discusión" : "Eliminar mi discusión"}
                >
                  <Trash2 size={16} />
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};
